"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { trackViewContent } from "@/lib/analytics";

type ProductId = "genigas" | "erp" | "hr";

type Product = {
  id: ProductId;
  /** Product site, when one exists. Without it the card reads "coming soon". */
  href?: string;
  featureKeys: string[];
  icon: React.ReactNode;
};

const PRODUCTS: Product[] = [
  {
    id: "genigas",
    href: "https://genigas.moaaleme.genitech.dev",
    featureKeys: ["maintenance", "compliance", "documents", "assets", "inspections"],
    icon: (
      <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden="true">
        <path d="M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M3 21h12M7 7h4M7 11h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M14 10h3a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0v-7l-2.5-2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "erp",
    featureKeys: ["sales", "inventory", "purchasing", "accounting"],
    icon: (
      <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="13" y="3" width="8" height="5" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="13" y="10" width="8" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <rect x="3" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    ),
  },
  {
    id: "hr",
    featureKeys: ["employees", "attendance", "leave", "payroll"],
    icon: (
      <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="17.5" cy="9.5" r="2.4" stroke="currentColor" strokeWidth="1.7" />
        <path d="M16 19.5a4.5 4.5 0 0 1 5.5-4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
];

/** GeniTech's own systems, as opposed to the client services above. */
export default function ProductsSection() {
  const t = useTranslations("Products");
  const locale = useLocale();
  const isArabic = locale === "ar";

  return (
    <section
      id="products"
      dir={isArabic ? "rtl" : "ltr"}
      className="relative px-4 py-14 md:px-6 lg:px-8 lg:py-20"
    >
      <div className="mx-auto flex w-full max-w-290 flex-col gap-10">
        <header className="flex flex-col gap-3 items-start text-start">
          <h2 className="m-0 bg-[linear-gradient(89.18deg,#0CA5F0_15.53%,#FFFFFF_220.84%)] bg-clip-text text-[30px] font-black leading-[1.3] text-transparent lg:text-[40px]">
            {t("title")}
          </h2>
          <p className="m-0 max-w-170 text-[16px] leading-[1.9] text-white/65">{t("subtitle")}</p>
        </header>

        <div className="divide-y divide-white/8 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02]">
          {PRODUCTS.map((product) => {
            const available = Boolean(product.href);

            return (
              <div
                key={product.id}
                className={`relative flex flex-col gap-5 p-6 transition-colors duration-300 hover:bg-white/[0.03] lg:flex-row lg:items-center lg:gap-8 lg:p-8 ${
                  available ? "bg-[linear-gradient(270deg,rgba(90,107,224,0.14),transparent_55%)]" : ""
                }`}
              >
                {/* Identity */}
                <div className="flex shrink-0 items-center gap-4 lg:w-60">
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#1b2436] text-cyan-400">
                    {product.icon}
                  </span>
                  <div className="text-start">
                    <h3 className="m-0 text-[22px] font-black leading-tight text-white">
                      {t(`items.${product.id}.name`)}
                    </h3>
                    <p className="m-0 text-[13px] text-white/55">{t(`items.${product.id}.tagline`)}</p>
                  </div>
                </div>

                {/* Description + capability chips */}
                <div className="flex flex-1 flex-col gap-3 lg:text-start">
                  <p className="m-0 text-[15px] leading-[1.85] text-white/70">
                    {t(`items.${product.id}.description`)}
                  </p>
                  <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                    {product.featureKeys.map((key) => (
                      <li
                        key={key}
                        className="inline-flex items-center rounded-full border border-white/10 bg-[#141b2b] px-3 py-1 text-[12px] text-white/70"
                      >
                        {t(`items.${product.id}.features.${key}`)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action */}
                <div className="shrink-0">
                  {available ? (
                    <Link
                      href={product.href!}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackViewContent({ content_name: product.id, content_category: "product" })
                      }
                      className="inline-flex min-h-11 items-center justify-center rounded-full bg-white px-6 text-[15px] font-bold text-[#13192B] transition-opacity duration-300 hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
                    >
                      {t("visit")}
                    </Link>
                  ) : (
                    <span className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 px-6 text-[15px] font-medium text-white/60">
                      {t("comingSoon")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
